import React from 'react';
import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import './styles/delivery-map.css';

interface DeliveryMapProps {
  warehouseLocation?: { lat: number; lng: number };
  userLocation?: { lat: number; lng: number };
  warehouseName?: string;
  deliveryAddress?: string;
}

const DeliveryMap: React.FC<DeliveryMapProps> = ({
  warehouseLocation,
  userLocation,
  warehouseName = 'Warehouse',
  deliveryAddress = 'Delivery Address'
}) => {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    id: 'delivery-map-loader',
  });

  // Validate coordinates
  const isValidCoord = (location?: { lat: number; lng: number }) => {
    return location && typeof location.lat === 'number' && typeof location.lng === 'number' && isFinite(location.lat) && isFinite(location.lng);
  };

  const validWarehouse = isValidCoord(warehouseLocation) ? warehouseLocation : null;
  const validUser = isValidCoord(userLocation) ? userLocation : null;

  // Calculate center - center between both points
  const getMapCenter = () => {
    if (validWarehouse && validUser) {
      return {
        lat: (validWarehouse.lat + validUser.lat) / 2,
        lng: (validWarehouse.lng + validUser.lng) / 2
      };
    }
    if (validUser) return { lat: validUser.lat, lng: validUser.lng };
    if (validWarehouse) return { lat: validWarehouse.lat, lng: validWarehouse.lng };
    return { lat: 28.6139, lng: 77.2090 };
  };

  // Route points
  const routePoints = [];
  if (validWarehouse) routePoints.push(validWarehouse);
  if (validUser) routePoints.push(validUser);

  if (!isLoaded || loadError) {
    return (
      <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', borderRadius: '12px' }}>
        {loadError ? 'Failed to load map' : 'Loading map...'}
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '300px', borderRadius: '12px' }}
      center={getMapCenter()}
      zoom={17}
      options={{ zoomControl: true, streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}
    >
      {/* Route line */}
      {routePoints.length === 2 && (
        <Polyline
          path={routePoints}
          options={{ strokeColor: '#2196F3', strokeOpacity: 0.8, strokeWeight: 3, geodesic: true }}
        />
      )}

      {/* Warehouse marker - green pin */}
      {validWarehouse && (
        <Marker
          position={validWarehouse}
          title={warehouseName}
          label={{
            text: 'W',
            color: '#fff',
            fontSize: '12px',
            fontWeight: 'bold',
          }}
          icon={{
            path: 'M12 2C6.48 2 2 6.48 2 12c0 4.42 6 14 10 16.66 4-2.66 10-12.24 10-16.66C22 6.48 17.52 2 12 2zm0 12c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z',
            fillColor: '#4CAF50',
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 2,
            scale: 2,
          }}
        />
      )}

      {/* User location marker - blue pin */}
      {validUser && (
        <Marker
          position={validUser}
          title={deliveryAddress}
          icon={{
            path: 'M12 2C6.48 2 2 6.48 2 12c0 4.42 6 14 10 16.66 4-2.66 10-12.24 10-16.66C22 6.48 17.52 2 12 2zm0 12c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z',
            fillColor: '#2196F3',
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 2,
            scale: 2,
          }}
        />
      )}
    </GoogleMap>
  );
};

export default DeliveryMap;
